import { t } from "@lingui/core/macro";
import { deduplicatePrimitiveArray } from "@/Utils/arrayUtils";
import { queryOptions, useQuery } from "@tanstack/react-query";
import type { z } from "zod";
import { calendarDateSchema } from "@medoma/models/date-and-time";
import { logisticsApi } from "./ApiClient";
import { generateQueryString } from "./Helpers";
import {
  createDeletedPatient,
  fetchPatient,
  fetchPatients,
  resolvePatient,
} from "./Patients";
import {
  timeOfDayDictionary,
  timeOfDaySchema,
} from "@medoma/models/activities";
import { i18n } from "@lingui/core";
import { type IPatient } from "@medoma/models/patients";
import {
  unenrichedVisitSchema,
  type IUnenrichedVisit,
} from "@medoma/models/visits.unenriched";
import type { routeSchema, routesSchema } from "@medoma/models/routes";
import { type IRoute } from "@medoma/models/routes";
import {
  unenrichedRouteSchema,
  unenrichedRoutesSchema,
} from "@medoma/models/routes.unenriched";

const timeOfDay = (time: "Any" | Date) => {
  if (time === timeOfDaySchema.enum.Any) {
    return i18n._(timeOfDayDictionary.Any.short);
  }
  const hour = time.getHours();

  if (hour >= 4 && hour < 10) {
    return t`Morgon`;
  }

  if (hour >= 10 && hour < 12) {
    return t`Förmiddag`;
  }

  if (hour >= 12 && hour < 17) {
    return t`Eftermiddag`;
  }

  if (hour >= 17 && hour < 22) {
    return t`Kväll`;
  }

  if (hour >= 22 || (hour >= 0 && hour < 4)) {
    return t`Natt`;
  }

  return t`[tid på dagen]`;
};

const generateRouteName = (route: z.infer<typeof routeSchema>) => {
  const { visits } = route;
  const occurrences = visits.flatMap((visit) => visit.occurrences);
  if (visits.length === 0) {
    return t`Ny rutt`;
  }
  if (!occurrences[0]) {
    return t`Ny rutt`;
  }
  const specificTimeOccurrences = occurrences.filter(
    (occurrence) => occurrence.timeOfDay === "Specific",
  );
  if (!specificTimeOccurrences[0]) {
    return i18n._(timeOfDayDictionary.Any.short);
  }
  return timeOfDay(
    specificTimeOccurrences[0].timeOfDay === timeOfDaySchema.enum.Any
      ? timeOfDaySchema.enum.Any
      : specificTimeOccurrences[0].start,
  );
};

const nameRoutes = (possiblyNamelessRoutes: z.infer<typeof routesSchema>) => {
  return possiblyNamelessRoutes.map((route) => nameRoute(route));
};

export const nameRoute = (
  possiblyNamelessRoute: z.infer<typeof routeSchema>,
) => {
  const name = generateRouteName(possiblyNamelessRoute);
  return {
    ...possiblyNamelessRoute,
    name,
  };
};

const patientIdsInRoutes = (
  unenrichedRoutes: z.infer<typeof unenrichedRoutesSchema>,
) =>
  deduplicatePrimitiveArray(
    unenrichedRoutes
      .flatMap(({ visits }) => visits)
      .flatMap(({ patientId }) => patientId),
  );

export const parseAndEnrichRoutes = async (probableRoutes: unknown) => {
  const possiblyNamelessUnenrichedRoutes =
    unenrichedRoutesSchema.parse(probableRoutes);

  if (possiblyNamelessUnenrichedRoutes.length === 0) {
    return possiblyNamelessUnenrichedRoutes as [];
  }

  const patientIds = patientIdsInRoutes(possiblyNamelessUnenrichedRoutes);
  const patients = await fetchPatients({ patientIds });
  // The visits are already decoded; enrichment swaps patientId for the patient.
  const possiblyNamelessRoutes = possiblyNamelessUnenrichedRoutes.map(
    (route) => ({
      ...route,
      visits: route.visits.map(({ patientId, ...visit }) => ({
        ...visit,
        patient: resolvePatient({ patientId, patients }),
      })),
    }),
  );
  const namedRoutes = nameRoutes(possiblyNamelessRoutes);
  return namedRoutes;
};

export const routeKeys = {
  all: ["routes"] as const,
  lists: () => [...routeKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...routeKeys.lists(), { filters }] as const,
  detail: (id: string) => [...routeKeys.all, id, "details"] as const,
};

const VISIT_KEY_ROOT = "visits";
export const visitKeys = (routeId: string) => ({
  all: () => [...routeKeys.all, routeId, VISIT_KEY_ROOT] as const,
  lists: () => [...visitKeys(routeId).all(), "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...visitKeys(routeId).lists(), { filters }] as const,
  detail: (id: string) => [...visitKeys(routeId).all(), id, "details"] as const,
});

export async function fetchRoute(routeId: string) {
  const parsedRoute = await logisticsApi
    .get(`/ambulating/routes/${routeId}`)
    .json(unenrichedRouteSchema);
  const patientIds = patientIdsInRoutes([parsedRoute]);
  const patients = await fetchPatients({ patientIds });
  const possiblyNamelessRoute = {
    ...parsedRoute,
    visits: parsedRoute.visits.map(({ patientId, ...visit }) => ({
      ...visit,
      patient: resolvePatient({ patientId, patients }),
    })),
  };
  const namedRoute = nameRoute(possiblyNamelessRoute);
  const route = namedRoute;

  return route;
}

export const useUpcomingVisit = (routeId: string) => {
  return useQuery({
    queryKey: visitKeys(routeId).list({ status: "upcoming" }),
    queryFn: () =>
      fetchRoute(routeId).then((route) => {
        const ongoingVisits = route.visits.filter(
          ({ status }) => status === "travellingTo" || status === "ongoing",
        );
        return ongoingVisits.length === 0
          ? (route.visits.find((visit) => visit.status === "planned") ?? null)
          : null;
      }),
  });
};

export const useOngoingVisit = (routeId: string) => {
  return useQuery({
    queryKey: visitKeys(routeId).list({ status: "ongoing" }),
    queryFn: () => {
      return fetchRoute(routeId).then((route) => {
        return (
          route.visits.find(
            ({ status }) => status === "travellingTo" || status === "ongoing",
          ) ?? null
        );
      });
    },
  });
};

const enrichVisit = ({
  visit,
  patient,
  routeId,
}: {
  visit: IUnenrichedVisit;
  patient: IPatient;
  routeId: string;
}) => {
  const visitWithPatient = { ...visit, patient };
  const visitWithRouteReference = { ...visitWithPatient, routeId: routeId };
  return visitWithRouteReference;
};

async function fetchVisit(routeId: string, visitId: string) {
  const parsedVisit = await logisticsApi
    .get(`/ambulating/routes/${routeId}/visits/${visitId}`)
    .json(unenrichedVisitSchema);
  try {
    const patient = await fetchPatient(parsedVisit.patientId);
    return enrichVisit({ visit: parsedVisit, patient, routeId });
  } catch (_e) {
    const patient = createDeletedPatient(parsedVisit.patientId);
    return enrichVisit({ visit: parsedVisit, patient, routeId });
  }
}

export const useVisit = (routeId: string, visitId: string) => {
  return useQuery({
    queryKey: visitKeys(routeId).detail(visitId),
    queryFn: () => fetchVisit(routeId, visitId),
  });
};

async function fetchEmployeesRoutes(date: string) {
  const queryString = generateQueryString({
    date,
  });
  const parsedRoutes = await parseAndEnrichRoutes(
    await logisticsApi.get(`/ambulating/routes${queryString}`).json(),
  );
  return parsedRoutes;
}

export const employeesRoutesQueryOptions = (date: Date) => {
  const formattedDate = calendarDateSchema.encode(date);
  return queryOptions({
    queryKey: routeKeys.list({
      from: formattedDate,
      to: null,
      assignee: "me",
    }),
    queryFn: () => fetchEmployeesRoutes(formattedDate),
  });
};

export async function startTravellingToVisit(routeId: string, visitId: string) {
  await logisticsApi.post(
    `/ambulating/routes/${routeId}/visits/${visitId}/startTravellingTo`,
  );
}

export async function startVisit(routeId: string, visitId: string) {
  await logisticsApi.post(
    `/ambulating/routes/${routeId}/visits/${visitId}/start`,
  );
}

export async function finishVisit(routeId: string, visitId: string) {
  await logisticsApi.post(
    `/ambulating/routes/${routeId}/visits/${visitId}/finish`,
  );
}

export const cancelTravellingToVisit = async (
  routeId: string,
  visitId: string,
) => {
  await logisticsApi.post(
    `/ambulating/routes/${routeId}/visits/${visitId}/revertTravellingTo`,
  );
};

export const cancelStartedVisit = async (routeId: string, visitId: string) => {
  await logisticsApi.post(
    `/ambulating/routes/${routeId}/visits/${visitId}/revertStarted`,
  );
};

export const revertFinishedVisit = async (routeId: string, visitId: string) => {
  await logisticsApi.post(
    `/ambulating/routes/${routeId}/visits/${visitId}/revertFinished`,
  );
};

export const getRouteFromGroup = (routes: IRoute[], groupId: string) => {
  return routes.find((route) =>
    route.visits.find((visit) => visit.id === groupId),
  );
};
